/* eslint-disable camelcase */
import React from 'react';
import { asText } from '@lib/prismic/util/document';
import {
  IPrismicImage, IPrismicMetaKeyword, IPrismicText, IPrismicUrl
} from '@lib/prismic/types';
import DefaultTemplate from '@components/shared/templates/DefaultTemplate/DefaultTemplate';
import FullBleedHero from '@components/shared/Hero/FullBleedHero/FullBleedHero';
import { useTheme } from '@components/theme';
import MarketPageHero from '@components/shared/MarketPageHero/MarketPageHero';
import classNames from 'classnames/bind';
import Packages from '@components/shared/Packages/Packages';
import PackageInclusions from '../Pricing/components/PackageInclusions/PackageInclusions';
import HalfImageHalfText from './HalfImageHalfText/HalfImageHalfText';
import styles from './giftPage.module.scss';
import PackageCards from './PackageCards/PackageCards';

interface IPrismicData {
  inclusion: IInclusionProps[];
  main_hero_image: IPrismicImage;
  hero_subtitle:IPrismicText[];
  hero_title: IPrismicText[];
  how_to_gift_title: IPrismicText[];
  how_to_gift_image: IPrismicImage;
  how_to_options: IOptionProps[];
  meta_description: string;
  meta_keywords: IPrismicMetaKeyword[];
  meta_title: string;
  redeem_main_image: IPrismicImage;
  redeem_small_image: IPrismicImage;
  redeem_subtitle: IPrismicText[];
  redeem_title: IPrismicText[];
  redeem_cta_text: IPrismicText[];
  redeem_cta_link: IPrismicUrl;

}

interface IInclusionProps {
    description: IPrismicText[];
    title: IPrismicText[];
    image: IPrismicImage;
}

interface IGiftProps {
  prismicData: IPrismicData;
}

interface IOptionProps {
    subtitle: IPrismicText[];
    description: IPrismicText[];
}

const Gift = ({
  prismicData,
}: IGiftProps) => {
  const {
    inclusion,
    main_hero_image,
    meta_description,
    meta_keywords,
    meta_title,
    hero_subtitle,
    hero_title,
    how_to_gift_title,
    how_to_gift_image,
    how_to_options,
    redeem_cta_link,
    redeem_cta_text,
    redeem_main_image,
    redeem_small_image,
    redeem_subtitle,
    redeem_title,
  } = prismicData;
  const theme = useTheme();

  return (
    <DefaultTemplate
      data-test="Interior-design-gift-page"
      title={meta_title}
      description={meta_description}
      keywords={meta_keywords.map(({ meta_keyword }: any) => meta_keyword)}
      className={theme.styles.Body}
      navProps={{
        isTransparent: true,
        isFixed: true,
        isConflictingVariant: true
      }}
      canonicalLink={'/gift'}
    >
      <FullBleedHero
        hero_title={hero_title}
        hero_subtitle={hero_subtitle}
        main_hero_image={main_hero_image}
        subtitleIsH1
      />
      <Packages
        className={styles.PackageContainer}
        cardsClassName={styles.CardsWrapper}
        cards={<PackageCards />}
      />
      {
        !!inclusion?.length && (
          <PackageInclusions
            className={styles.PackageInclusions}
            inclusions={inclusion.map(({ description, image, title }: any) => ({
              image,
              title: asText(title),
              description: asText(description)
            }))}
          />
        )
      }

      <HalfImageHalfText
        image={how_to_gift_image}
        backgroundColor={theme.colors.core6}
        textWrapperClass={styles.TextWrapper}
      >
        <>
          <h2
            className={classNames(theme.styles.Title1, styles.HowToGiftTitle)}
          >
            {asText(how_to_gift_title)}
          </h2>
          <div>
            {how_to_options.map(({ description, subtitle }: IOptionProps) => {
              return (
                <div key={asText(subtitle)} className={styles.HowToOptions}>
                  <h3
                    className={classNames(
                      theme.styles.Overline,
                      styles.HowToGiftSubTitle
                    )}
                  >
                    {asText(subtitle)}
                  </h3>
                  <p>{asText(description)}</p>
                </div>
              );
            })}
          </div>
        </>
      </HalfImageHalfText>
      <MarketPageHero
        title={asText(redeem_title)}
        subtitle={asText(redeem_subtitle)}
        ctaLink={redeem_cta_link.url}
        ctaText={asText(redeem_cta_text)}
        mainImage={redeem_main_image}
        smallImage={redeem_small_image}
        className={styles.RedeemContainer}
        bottomImagePos
      />
    </DefaultTemplate>
  );
};

export default Gift;
